
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Award, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface CaseStudyCardProps {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string;
  results: {
    label: string;
    value: string;
    change?: string;
    positive?: boolean;
  }[];
  link?: string;
  featured?: boolean;
}

export const CaseStudyCard = ({
  id,
  title,
  description,
  image,
  category,
  results,
  featured = false,
}: CaseStudyCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Create link to the case study detail page using the id
  const caseStudyUrl = `/case-studies/${id}`;

  return (
    <div
      className={cn(
        "group h-full flex flex-col rounded-xl overflow-hidden transition-all duration-300 glass-card shine-effect",
        featured ? "md:col-span-2" : "",
        isHovered ? "shadow-lg translate-y-[-5px]" : "shadow-card"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative">
        {featured && (
          <div className="absolute top-4 left-4 z-10 flex items-center rounded-full bg-indigo-500/90 text-white text-sm px-3 py-1 shadow-sm">
            <Award className="w-4 h-4 mr-1" />
            <span>Featured Case Study</span>
          </div>
        )}
        
        <div className="relative aspect-video overflow-hidden">
          <img
            src={image}
            alt={title}
            className={cn(
              "w-full h-full object-cover transition-transform duration-500",
              isHovered ? "scale-105" : ""
            )}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-warmGray-900/70 via-warmGray-900/30 to-transparent"></div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="inline-block px-3 py-1 bg-white/90 dark:bg-warmGray-800/90 backdrop-blur-sm rounded-full text-xs font-medium mb-3 shadow-sm">
            {category}
          </span>
          <h3 className="text-xl md:text-2xl font-bold text-white">{title}</h3>
        </div>
      </div>

      <div className="p-6 flex-grow flex flex-col">
        <p className="text-foreground/80 mb-6 line-clamp-3">{description}</p>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          {results.map((result, index) => (
            <div key={index} className="bg-white/50 dark:bg-warmGray-800/50 rounded-lg p-3 border border-indigo-100 dark:border-indigo-900">
              <div className="text-2xl font-bold text-gradient bg-primary-gradient">
                {result.value}
              </div>
              {result.change && (
                <div className={cn(
                  "text-sm font-medium",
                  result.positive ? "text-teal-600 dark:text-teal-400" : "text-rose-600 dark:text-rose-400"
                )}>
                  {result.positive ? "+" : ""}{result.change}
                </div>
              )}
              <div className="text-sm text-foreground/70">{result.label}</div>
            </div>
          ))}
        </div>
        
        <div className="mt-auto">
          <Link 
            to={caseStudyUrl}
            className="group inline-flex items-center text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
          >
            Read Full Case Study
            <ChevronRight className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyCard;
